/**
 * Mental State Definitions
 * Defines all mental states and their evolution patterns
 */

export const mentalStates = {
  // Emotional States (Most responsive to empathy/handling)
  emotional: {
    Frustrated: {
      difficulty: "hard",
      initial: "Express frustration with current situation",
      triggers: [
        "acknowledges pain points",
        "offers clear solutions",
        "shows path forward",
      ],
      evolution: "Become more constructive as solutions emerge",
      fallback: "Stay focused on practical outcomes",
    },
    Defensive: {
      difficulty: "hard",
      initial: "Show resistance to aggressive or presumptive approaches",
      triggers: [
        "builds trust",
        "shows respect",
        "takes consultative approach",
      ],
      evolution: "Become more open as trust builds",
      fallback: "Maintain healthy skepticism",
    },
  },

  // Professional States (More stable, but can be enhanced)
  professional: {
    Analytical: {
      difficulty: "medium",
      initial: "Take data-driven, logical approach",
      triggers: [
        "provides concrete evidence",
        "shares detailed analysis",
        "demonstrates clear value with numbers",
      ],
      evolution: "Become more convinced through strong evidence",
      fallback: "Maintain analytical mindset",
    },
    Cautious: {
      difficulty: "medium",
      initial: "Take careful, risk-aware approach to decisions",
      triggers: [
        "builds trust gradually through consistent understanding",
        "shows patience with your pace",
        "demonstrates they've thought through the risks too",
      ],
      evolution: "Become more confident as trust accumulates",
      fallback: "Maintain careful evaluation mindset",
    },
    Neutral: {
      difficulty: "medium",
      initial: "Listen openly without bias or prior commitment",
      triggers: [
        "presents clear relevance",
        "maintains balanced dialogue",
        "addresses needs directly",
      ],
      evolution: "Shift toward interest as value becomes clear",
      fallback: "Return to uncommitted listening",
    },
  },

  // Situational States (Can shift based on value demonstration)
  situational: {
    Skeptical: {
      difficulty: "medium",
      initial: "Show doubt about claims and promises",
      triggers: [
        "speaks with confidence that feels earned, not rehearsed",
        "language quality suggests they've solved this before",
        "responses converge into a coherent picture over time",
      ],
      evolution: "Become more trusting as accumulated signals suggest competence",
      fallback: "Maintain healthy skepticism",
    },
    Curious: {
      difficulty: "easy",
      initial: "Show interest in learning more",
      triggers: [
        "provides insights",
        "shares innovative approaches",
        "demonstrates expertise",
      ],
      evolution: "Become more excited as potential is shown",
      fallback: "Maintain interest in valuable aspects",
    },
  },

  // Positive States (Easiest to work with)
  positive: {
    Friendly: {
      difficulty: "easy",
      initial: "Show warmth and openness to conversation",
      triggers: [
        "builds rapport",
        "shows genuine interest",
        "maintains pleasant dialogue",
      ],
      evolution: "Become more engaged and collaborative",
      fallback: "Maintain friendly demeanor",
    },
    Collaborative: {
      difficulty: "easy",
      initial: "Show willingness to work together on solutions",
      triggers: [
        "seeks joint problem-solving",
        "values partnership approach",
        "co-creates solutions",
      ],
      evolution: "Become more invested in mutual success",
      fallback: "Maintain collaborative mindset",
    },
  },

  // Challenging States (Hardest to work with)
  challenging: {
    Hostile: {
      difficulty: "hard",
      initial: "Show antagonism and active resistance",
      triggers: [
        "demonstrates exceptional professionalism",
        "refuses to engage with hostility",
        "provides undeniable value",
      ],
      evolution: "Potentially soften if handled with grace",
      fallback: "Maintain defensive posture",
    },
  },
};

/**
 * Get mental state details by name
 * 
 * @param {string} mentalStateName - Name of the mental state
 * @returns {Object|null} - State details or null if not found
 */
export function getMentalStateDetails(mentalStateName) {
  if (!mentalStateName || typeof mentalStateName !== "string") return null;

  // Matched case-insensitively on purpose. The stored attitude reaches this
  // lookup from several places that do not agree on casing — the baseline
  // analyzer asks the model to echo the attitude back and its prompt examples
  // are lowercase ("curious/skeptical/frustrated") — and an exact-case miss
  // here degrades silently to a weaker prompt rather than erroring.
  const wanted = mentalStateName.trim().toLowerCase();
  if (!wanted) return null;

  for (const [category, states] of Object.entries(mentalStates)) {
    for (const [name, details] of Object.entries(states)) {
      if (name.toLowerCase() === wanted) {
        // Always return the canonical name, not the caller's casing.
        return { ...details, category, name };
      }
    }
  }

  return null;
}

/**
 * Normalize an attitude supplied by a caller into the object shape the baseline
 * builder expects.
 *
 * The baseline pipeline reads `mentalState.state`, but every live caller passes
 * a bare string (the value stored on RolePlay.mentalState). Those callers used
 * to be coerced to `{}`, which silently dropped the attitude before it could
 * reach the prompt.
 *
 * @param {string|Object|null|undefined} value
 * @returns {Object} - `{ state?: string, engagement?: string }`
 */
export function normalizeMentalStateInput(value) {
  if (!value) return {};

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? { state: trimmed } : {};
  }

  if (typeof value === "object" && !Array.isArray(value)) return value;

  return {};
}

/**
 * Get all mental state names
 *
 * @returns {Array<string>} - Array of mental state names
 */
export function getAllMentalStateNames() {
  const names = [];

  for (const states of Object.values(mentalStates)) {
    names.push(...Object.keys(states));
  }

  return names;
}

/**
 * Get mental states in UI-friendly format for dropdowns/selectors
 *
 * @returns {Array<Object>} - Array of objects with value, name, difficulty
 */
export function getMentalStatesForUI() {
  const states = [];

  for (const [category, categoryStates] of Object.entries(mentalStates)) {
    for (const [name, details] of Object.entries(categoryStates)) {
      states.push({
        value: name,
        name: name,
        difficulty: details.difficulty || "medium",
      });
    }
  }

  // Sort by difficulty (easy first, then medium, then hard)
  const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
  states.sort((a, b) => {
    const orderDiff = difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
    if (orderDiff !== 0) return orderDiff;
    // If same difficulty, sort alphabetically
    return a.name.localeCompare(b.name);
  });

  return states;
}
